import { formatDate } from "@marekl/adminer/format";
import { parseApiDate } from "@marekl/adminer/datetime";
import type { PublicSeason, RegistrationFlow } from "../../../lib/queries/registration";

/**
 * The three doors. Each one is a route of its own, so a federation can send
 * a club straight to the form it means; the chooser is what somebody sees
 * who arrives without a link.
 *
 * The description says what the club GETS, not what the form will ask for.
 * A list of required fields is a reason to close the page, and it is on the
 * next screen anyway.
 */
export const FLOW_CHOICES: {
  value: RegistrationFlow;
  path: string;
  title: string;
  description: string;
  /** Not offered on the chooser; reachable only by its own link. */
  unlisted?: boolean;
}[] = [
  {
    value: "cmf_member",
    path: "/registrace-tymu/clen-cmf",
    title: "Člen ČNSMS",
    description:
      "Členství v ČNSMS: registrace do sezóny, start na MČR i na pohárových soutěžích a ČNSMS ID pro členy týmu.",
  },
  {
    value: "cup_only",
    path: "/registrace-tymu/poharovky",
    title: "Jen pohárové soutěže",
    description: "Přihlášky na pohárové soutěže, bez registrace do sezóny.",
  },
  {
    value: "foreign",
    path: "/registrace-tymu/zahranicni-klub",
    title: "Zahraniční tým",
    description:
      "Registrace zahraničních týmů na soutěže WMS.",
    // Offered by a link from the federation, not from the public chooser:
    // the Czech doors are what a Czech club needs to see first.
    unlisted: true,
  },
];

/** Every flow's own path, for the router. */
export const REGISTRATION_FLOW_PATHS = FLOW_CHOICES.map((c) => c.path);

/** The flow a path names, or null on the chooser itself. */
export function flowForPath(pathname: string): RegistrationFlow | null {
  return FLOW_CHOICES.find((c) => c.path === pathname)?.value ?? null;
}

/**
 * When a cup-only club can still become a full member, as a sentence.
 *
 * The window is the same one that gates a soupiska, so while it is open the
 * club is told the date it has to decide by; once it has closed the answer
 * is the next season, and naming a date that has passed would be worse than
 * naming none. Unknown season (no active one, or the read failed) says the
 * plain thing that is true either way.
 */
export function upgradeNote(season: PublicSeason | undefined): string {
  if (!season) return "Členem ČNSMS se můžete stát i později.";
  const to = season.registration_open_to;
  if (season.phase === "registration_open" && to) {
    return `Členem ČNSMS se můžete stát do ${formatDate(parseApiDate(to))}.`;
  }
  if (season.phase === "registration_open") {
    return `Členem ČNSMS se můžete stát ještě v sezóně ${season.code}.`;
  }
  // Before the window opens, „v příští sezóně" contradicts the alert
  // beside it, which names the date this season's registration starts.
  // The season the club can still join is this one; it just cannot start
  // yet.
  const from = season.registration_open_from;
  if (season.phase === "pre_registration" && from) {
    return `Členem ČNSMS se můžete stát od ${formatDate(parseApiDate(from))}.`;
  }
  if (season.phase === "pre_registration") {
    return `Členem ČNSMS se můžete stát, až se registrace do sezóny ${season.code} otevře.`;
  }
  return "Členem ČNSMS se můžete stát v příští sezóně.";
}

/**
 * Why „Člen ČNSMS" is not available right now, or null while it is.
 *
 * Registration into a season runs in a window, and outside it a new club
 * would be approved into a season it cannot register a soupiska for. The
 * SERVER still accepts such a sign-up on purpose (onboarding is not gated
 * by the window); this is the chooser declining to start something that
 * cannot finish, and it says when it can.
 */
export function memberBlockedReason(season: PublicSeason | undefined): string | null {
  if (!season) return null;
  if (season.phase === "registration_open") return null;
  const openFrom = season.registration_open_from;
  if (season.phase === "pre_registration" && openFrom) {
    return `Registrace do sezóny ${season.code} se otevře ${formatDate(parseApiDate(openFrom))}. Do té doby tým zaregistrujte pro pohárové soutěže.`;
  }
  return `Registrace do sezóny ${season.code} je uzavřená. Tým můžete zaregistrovat pouze pro pohárové soutěže.`;
}

/** Every listed door is closed by an administrator (service/access). */
export const ALL_CLOSED_MESSAGE =
  "Registrace nových týmů je momentálně uzavřena.";

/**
 * Why a door an administrator closed does not open. No date: unlike the
 * season window, a closed door has none to promise.
 */
export function closedReason(titles: string[]): string {
  const named = titles.map((t) => `„${t}“`).join(" a ");
  return titles.length > 1
    ? `Registrace ${named} jsou momentálně uzavřeny.`
    : `Registrace ${named} je momentálně uzavřena.`;
}
